import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast, Toaster } from 'sonner';
import { Download, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { betaAdminApi } from './betaAdminApi';
import { BetaOverview } from './BetaOverview';
import { BetaTesterRow } from './BetaTesterRow';
import { BetaApplicationView } from './BetaApplicationView';
import { BetaTestimonialEditor } from './BetaTestimonialEditor';
import { BetaActions } from './BetaActions';

const FILTERS = [['all', 'All'], ['active', 'Active'], ['ending_soon', 'Ending soon'], ['ended', 'Ended'], ['lifetime', 'Free for life'], ['removed', 'Removed']];

export const BetaProgramAdmin = ({ adminToken }) => {
  const api = useMemo(() => betaAdminApi(adminToken), [adminToken]);
  const [o, setO] = useState(null);
  const [testers, setTesters] = useState([]);
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try { const [ov, ts] = await Promise.all([api.overview(), api.testers()]); setO(ov); setTesters(ts); }
    catch { toast.error('Could not load the beta program'); }
    setLoading(false);
  }, [api]);
  useEffect(() => { reload(); }, [reload]);

  const shown = testers.filter((t) => filter === 'all' || t.status === filter);
  return (
    <div className="space-y-6" data-testid="beta-program-admin">
      <Toaster richColors position="top-right" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Beta Program</h2>
          <p className="text-sm text-slate-500">90 days free · free for life with testimonial, photo and a 5-star review. Reminders go out automatically 14 and 7 days before the end and on the day it ends.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href="/beta" target="_blank" rel="noopener noreferrer" className="inline-flex items-center h-9 px-3 rounded-md border text-sm" data-testid="beta-open-page-link"><ExternalLink className="w-4 h-4 mr-1" />Open /beta</a>
          <Button variant="outline" size="sm" onClick={() => api.exportCsv()} data-testid="beta-export-btn"><Download className="w-4 h-4 mr-1" />Export spreadsheet</Button>
          <Button variant="outline" size="sm" onClick={reload} data-testid="beta-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
        </div>
      </div>
      <BetaOverview o={o} onSaveSpots={async (n) => {
        try { await api.setSpots(n); toast.success(`Beta spots set to ${n}`); reload(); }
        catch { toast.error('Spots must be a number from 1 to 500'); }
      }} />
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-sm border ${filter === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white'}`} data-testid={`beta-filter-${k}`}>{l}</button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl divide-y" data-testid="beta-tester-list">
        {loading ? <p className="p-6 text-slate-500">Loading…</p> : !shown.length ? <p className="p-6 text-slate-500" data-testid="beta-empty">No beta testers here yet.</p> : shown.map((t) => (
          <div key={t.practiceId}>
            <BetaTesterRow t={t} open={open === t.practiceId} onToggle={() => setOpen(open === t.practiceId ? null : t.practiceId)} />
            {open === t.practiceId && (
              <div className="px-4 pb-5 pt-2 bg-slate-50/60 space-y-5" data-testid={`beta-tester-detail-${t.practiceId}`}>
                <BetaActions t={t} api={api} reload={reload} />
                <div className="grid lg:grid-cols-2 gap-6">
                  <BetaApplicationView t={t} />
                  <BetaTestimonialEditor key={`${t.practiceId}-${t.betaProgram.photoAt || ''}`} t={t} api={api} reload={reload} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
